import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DecimalCalculator = () => {
  const [operation, setOperation] = useState('add');
  const [number1, setNumber1] = useState('');
  const [number2, setNumber2] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleOperationChange = (e) => {
    setOperation(e.target.value);
  };

  const handleNumber1Change = (e) => {
    setNumber1(e.target.value);
    setError('');
  };

  const handleNumber2Change = (e) => {
    setNumber2(e.target.value);
    setError('');
  };

  const calculateDecimal = () => {
    if (!number1 || !number2) {
      setError('Please enter both numbers.');
      return;
    }

    const num1 = parseFloat(number1);
    const num2 = parseFloat(number2);

    if (isNaN(num1) || isNaN(num2)) {
      setError('Please enter valid numbers.');
      return;
    }

    let calculatedValue = 0;

    switch (operation) {
      case 'add':
        calculatedValue = num1 + num2;
        break;
      case 'subtract':
        calculatedValue = num1 - num2;
        break;
      case 'multiply':
        calculatedValue = num1 * num2;
        break;
      case 'divide':
        if (num2 === 0) {
          setError('Division by zero is not allowed.');
          return;
        }
        calculatedValue = num1 / num2;
        break;
      default:
        setError('Please select a valid operation.');
        return;
    }

    setResult(calculatedValue);

    // Update chart data
    setChartData({
      labels: ['Number 1', 'Number 2', 'Result'],
      datasets: [
        {
          label: 'Decimal Calculation',
          data: [num1, num2, calculatedValue],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateDecimal();
  };

  return (
    <>
      <Helmet>
        <title>Decimal Calculator | BioLab Tools</title>
        <meta name="description" content="Perform arithmetic operations on decimal numbers with ease. Add, subtract, multiply, and divide decimals accurately." />
        <meta name="keywords" content="decimal calculator, add decimals, subtract decimals, multiply decimals, divide decimals" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🔍</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Decimal Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Perform arithmetic operations on decimal numbers with ease. Add, subtract, multiply, and divide decimals accurately.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Operation</label>
                <select
                  name="operation"
                  value={operation}
                  onChange={handleOperationChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="add">Add (+)</option>
                  <option value="subtract">Subtract (-)</option>
                  <option value="multiply">Multiply (×)</option>
                  <option value="divide">Divide (÷)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number 1</label>
                <input
                  type="number"
                  value={number1}
                  onChange={handleNumber1Change}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter first number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number 2</label>
                <input
                  type="number"
                  value={number2}
                  onChange={handleNumber2Change}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter second number"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {result !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">{result.toFixed(4)}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Value' }
                            },
                            x: { title: { display: true, text: 'Category' } }
                          },
                          plugins: { title: { display: true, text: 'Decimal Calculation' } }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What types of operations are supported?</h3>
                <p className="text-gray-600">
                  The Decimal Calculator supports addition, subtraction, multiplication, and division of decimal numbers.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate are the calculations?</h3>
                <p className="text-gray-600">
                  The calculations are highly accurate, using standard mathematical formulas. Results are rounded to four decimal places.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for educational purposes?</h3>
                <p className="text-gray-600">
                  Yes, this calculator is perfect for educational purposes, helping students understand and practice decimal operations.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Decimal Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Select Operation</h3>
                <p className="text-gray-600">
                  Choose the operation you need: add, subtract, multiply, or divide.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Numbers</h3>
                <p className="text-gray-600">
                  Input the two decimal numbers you want to operate on.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Calculate</h3>
                <p className="text-gray-600">
                  Click the "Calculate" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the calculation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DecimalCalculator;